import type { ColumnDef } from '@tanstack/react-table';
import {
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { formatDisplayDate } from '@/utils/date';
import type { WorkSummaryDcrRow, WorkSummaryExpenseRow } from '../types/dashboard.types';
import { ClaimedApprovedAmount } from './ClaimedApprovedAmount';
import { WorkStatusBadge } from './WorkStatusBadge';

const AREA_ROUTE_SEPARATOR = ' → ';
const LOCATION_SEPARATOR = ', ';

function joinPresent(parts: (string | null)[], separator: string): string {
  return parts.filter(Boolean).join(separator);
}

export function getWorkSummaryDcrColumns(): ColumnDef<WorkSummaryDcrRow, unknown>[] {
  return [
    createSerialColumn<WorkSummaryDcrRow>(),
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.dcrDate),
    },
    {
      id: 'institution',
      header: 'Institution',
      cell: ({ row }) => (
        <WrappingCellText>
          <StackedCellText
            primary={row.original.instituteName ?? <EmptyValue />}
            secondary={joinPresent(
              [row.original.thanaName, row.original.districtName, row.original.divisionName],
              LOCATION_SEPARATOR,
            )}
          />
        </WrappingCellText>
      ),
    },
    {
      id: 'meetPerson',
      header: 'Meet Person',
      cell: ({ row }) => row.original.meetPersonMobile ?? <EmptyValue />,
    },
    {
      id: 'area',
      header: 'Area / Comments',
      cell: ({ row }) => (
        <WrappingCellText>
          <StackedCellText
            primary={
              joinPresent([row.original.areaFrom, row.original.areaTo], AREA_ROUTE_SEPARATOR) || (
                <EmptyValue />
              )
            }
            secondary={
              row.original.comments ? (
                <ExpandableComment comment={row.original.comments} />
              ) : undefined
            }
          />
        </WrappingCellText>
      ),
    },
    {
      id: 'workType',
      header: 'Work Type',
      cell: ({ row }) => row.original.workType ?? <EmptyValue />,
    },
    {
      id: 'transport',
      header: 'Transport',
      cell: ({ row }) => row.original.transportType ?? <EmptyValue />,
    },
    {
      id: 'fare',
      header: 'Fare (Tk)',
      cell: ({ row }) => (
        <ClaimedApprovedAmount
          claimed={row.original.fareAmount}
          approved={row.original.approveFareAmount}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <WorkStatusBadge status={row.original.status} />,
    },
  ];
}

export function getWorkSummaryExpenseColumns(): ColumnDef<WorkSummaryExpenseRow, unknown>[] {
  return [
    createSerialColumn<WorkSummaryExpenseRow>(),
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.expenseDate),
    },
    {
      id: 'expenseType',
      header: 'Expense Type',
      cell: ({ row }) => row.original.expenseType ?? <EmptyValue />,
    },
    {
      id: 'cost',
      header: 'Cost (Tk)',
      cell: ({ row }) => (
        <ClaimedApprovedAmount
          claimed={row.original.amount}
          approved={row.original.approveAmount}
        />
      ),
    },
    {
      id: 'vendor',
      header: 'Vendor / Person',
      cell: ({ row }) => (
        <WrappingCellText>{row.original.vendor ?? <EmptyValue />}</WrappingCellText>
      ),
    },
    {
      id: 'comment',
      header: 'Comment',
      cell: ({ row }) =>
        row.original.comments ? (
          <WrappingCellText>
            <ExpandableComment comment={row.original.comments} />
          </WrappingCellText>
        ) : (
          <EmptyValue />
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <WorkStatusBadge status={row.original.status} />,
    },
  ];
}
